// Submit form to API

import axios from 'axios';
// import { showFormMessage } from './showFormMessage.ts';
import { FormData } from './interfaces/FormData.ts';
import { ServiceRequestData, ServiceRequestResponse } from './interfaces/ServiceRequestData.ts';

/**
 * Show optimistic success message immediately
 */
const showOptimisticMessage = (form: HTMLFormElement): void => {
  const messageElement = form.querySelector('#form-message') as HTMLElement;
  if (!messageElement) return;

  messageElement.textContent = 'Спасибо! Заявка уже отправляется ✅ Мы скоро свяжемся с вами по указанным контактам.';
  messageElement.className = 'form-message form-message--success';
  messageElement.setAttribute('aria-live', 'polite');
  messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

/**
 * Show error message
 */
const showErrorMessage = (form: HTMLFormElement): void => {
  const messageElement = form.querySelector('#form-message') as HTMLElement;
  if (!messageElement) return;

  messageElement.textContent = 'Не удалось отправить заявку ❌ Проверьте соединение и попробуйте ещё раз.';
  messageElement.className = 'form-message form-message--error';
  messageElement.setAttribute('aria-live', 'polite');
  messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

/**
 * Prepare service request data from form data
 */
const prepareServiceRequestData = (data: FormData): ServiceRequestData => {
  return {
    name: data.name || '',
    email: data.email || '',
    phone: data.phone || '',
    service_name: data.service || '',
    comment: data.comment || undefined,
  };
};

/**
 * Validate service request data
 */
const validateServiceRequestData = (data: ServiceRequestData): boolean => {
  return !!(
    data.name &&
    data.email &&
    data.phone &&
    data.service_name
  );
};

export const submitForm = async (form: HTMLFormElement, data: FormData): Promise<void> => {
  // Honeypot pre-check (also enforced server-side)
  const honeypot = form.querySelector('.form-honeypot') as HTMLInputElement | null;
  if (honeypot && honeypot.value) {
    console.log('Bot detected');
    return;
  }

  // Timing pre-check (also enforced server-side via a signed token)
  const formStartTime = parseInt(form.dataset.startTime || '0', 10);
  const timeSpent = Date.now() - formStartTime;
  if (timeSpent < 3000) {
    console.log('Form filled too quickly');
    return;
  }

  // Prepare and validate service request data
  const serviceRequestData = prepareServiceRequestData(data);
  if (!validateServiceRequestData(serviceRequestData)) {
    showErrorMessage(form);
    throw new Error('Validation failed');
  }

  // Attach anti-spam fields so the server can verify them: the honeypot decoy
  // value and the signed time-trap token that was issued when the page rendered.
  const formTs = (form.querySelector('input[name="form_ts"]') as HTMLInputElement | null)?.value ?? '';
  (serviceRequestData as any)['website'] = honeypot?.value ?? '';
  (serviceRequestData as any)['form_ts'] = formTs;

  // Show optimistic message immediately
  showOptimisticMessage(form);

  try {
    // Send request to API
    const response = await axios.post<ServiceRequestResponse>('/api/service-requests', serviceRequestData, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Check response
    if (response.status === 202 || response.status === 200) {
      if (response.data.ok) {
        // Success - form is already showing optimistic message
        // Clear form after successful submission
        form.reset();
      } else {
        throw new Error('Server returned error');
      }
    } else {
      throw new Error(`Unexpected status: ${response.status}`);
    }
  } catch (error: any) {
    console.error('Form submission error:', error);

    // Check if it's an anti-spam / validation rejection
    if (error.response?.status === 422) {
      const errors = error.response.data?.errors;
      if (errors?.['form']) {
        showFormError(form, errors['form']?.[0] || 'Не удалось отправить заявку. Обновите страницу и попробуйте снова.');
        throw error;
      }
    }

    // Show error message
    showErrorMessage(form);

    // Form is NOT cleared - user can retry without losing data
    // Re-throw error so form-validation.ts can handle button state
    throw error;
  }
};

/**
 * Show a generic form-level error message (e.g. anti-spam rejection)
 */
const showFormError = (form: HTMLFormElement, message: string): void => {
  // Remove existing form-level error
  const existingError = form.querySelector('.form-error--recaptcha');
  if (existingError) {
    existingError.remove();
  }

  // Find submit button to insert error after it
  const submitButton = form.querySelector('#submit-button') || form.querySelector('button[type="submit"]');
  if (submitButton && submitButton.parentElement) {
    const errorElement = document.createElement('div');
    errorElement.className = 'form-error form-error--recaptcha';
    errorElement.setAttribute('role', 'alert');
    errorElement.textContent = message;
    submitButton.parentElement.insertBefore(errorElement, submitButton.nextSibling);
  }
};
